// modules
import math from 'exact-math';
// typings
import { Holding } from 'modules/TradesPW3/Holdings/typings';
// constants
import { DEFAULT_HOLDING_DETAILS } from 'modules/TradesPW3/Holdings/constants';
// services
import FormatService from 'modules/Format/service';
import LiveDataService from 'modules/LiveData';
// store
import HoldingsStore from 'modules/TradesPW3/Holdings/store';

class HoldingCalculations {
  getHoldingLiveData = (holding: Holding) => {
    const instrumentKey = HoldingsStore.getPrimaryInstrumentKey(holding);
    const liveData = LiveDataService.getByInstrumentKey(instrumentKey || '');
    const ltp = liveData?.lastTradedPrice || '0';

    return {
      liveData,
      ltp,
    };
  };

  generateHoldingDetails = (holding: Holding) => {
    if (holding) {
      const { needToCalculateData, ltp } = this.calculationBaseData(holding);
      const netQnty = this.calculateNetQuantity(holding);
      const {
        currentValue = '--',
        investment = '--',
        dayPnl = '--',
        dayPercentage = '--',
        overall = '--',
        overallPercentage = '--',
      } = needToCalculateData
        ? this.getHoldingPnlValues(netQnty, holding, ltp)
        : {};

      return {
        netQnty,
        usedQnty: holding.usedQty,
        totalQnty:
          (holding.fillInfo?.t1?.qty + holding.fillInfo?.demat?.qty) || 0,
        dematQty: this.calculateHoldingQuantity(holding, false) || 0,
        t1Qty: this.calculateHoldingQuantity(holding, true) || 0,
        day: dayPnl,
        dayPercentage,
        overall,
        overallPercentage,
        investment,
        currentValue,
        haircut: holding.additionalInfo?.haircut || 0,
        collateralQnty: holding.additionalInfo?.collateral_qty || 0,
        collateralValue: ltp
          ? this.calculateColleteralValue(holding, ltp)
          : '-',
      };
    }

    return DEFAULT_HOLDING_DETAILS;
  };

  private calculationBaseData = (holding: Holding) => {
    const { ltp } = this.getHoldingLiveData(holding);

    const notNullLtp = ltp || '';
    const needToCalculateData = holding && notNullLtp;

    return {
      ltp,
      notNullLtp,
      needToCalculateData,
    };
  };

  /**
   * Calculate holding quantity based on the type of holding
   * @param  {Holding} holding holding to calculate quantity
   * @param  {boolean} isT1 whether the holding is t1 or not
   */
  calculateHoldingQuantity = (holding: Holding, isT1: boolean) => {
    const { fillInfo, netInfo } = holding;
    const netQuantity = math.sub(netInfo.buyQty, netInfo.sellQty) ?? 0;
    const t1Quantity = fillInfo?.t1?.qty ?? 0;

    if (isT1) {
      /**
       * If t1 Holding is sold we have to use the netQuantity and that change
       * will not be reflecting in t1Quantity
       */
      return netQuantity < t1Quantity ? netQuantity : t1Quantity;
    }

    return t1Quantity && netQuantity
      ? math.sub(netQuantity, t1Quantity)
      : netQuantity;
  };

  /**
   * Calculates net qnty for holding
   * @param  {Holding} holding holding to calculate net_qnty
   */
  calculateNetQuantity = (holding: Holding) => {
    const { netInfoV3 } = holding || {};
    return netInfoV3.buyQty - netInfoV3.sellQty;
  };

  /**
   * Calculate currValue
   * @param  {number} netQnty
   * @param  {string} ltp ltp for current holding
   */
  calculateCurrValue = (netQnty: number, ltp: string) =>
    math.mul(netQnty, Number(ltp)).toFixed(2);

  /**
   * Calculate total investment
   * @param  {number} avgPrice
   * @param  {number} netQnty
   */
  calculateTotalInvestment = (avgPrice: number, netQnty: number) =>
    (avgPrice ? math.mul(avgPrice, netQnty).toFixed(2) : '--');

  /**
   * Calculate collateral value
   * @deprecated
   * @param  {Holding} holding
   * @param  {string} ltp ltp for current holding
   */
  calculateColleteralValue = (holding: Holding, ltp: string) =>
    Number(holding?.additionalInfo?.collateral_qty ?? 0) * Number(ltp);

  /**
   * Calculate close price of holding
   * @param  {Holding} holding
   * @param  {number} netQnty
   */
  calculateClose = (holding: Holding, netQnty: number) => {
    const { liveData } = this.getHoldingLiveData(holding) || {};
    const close = Number(liveData?.close || 1);
    return math.mul(netQnty, close);
  };

  /**
   * Calculates Day P&L
   * @param  {string} currentValue
   * @param  {number} close ltp for current holding
   */
  calculateDayPnL = (currentValue: string, close: number) => {
    if (Number(currentValue)) {
      return math.sub(Number(currentValue), close).toFixed(2);
    }

    return '0.00';
  };

  /**
   * Calculate percentage
   * @param  {number} numerator
   * @param  {number} denominator
   */
  calculatePercentage = (numerator: number, denominator: number) => {
    if (Number.isNaN(numerator) || Number.isNaN(denominator)) {
      return '--';
    }

    const division = math.div(numerator, denominator);

    if (Number.isNaN(division)) {
      return '0.00';
    }

    return Number.isFinite(division)
      ? math.mul(division, 100).toFixed(2)
      : '--';
  };

  /**
   * Calculate day percentage
   * @param  {string} dayPnl
   * @param  {number} close ltp for current holding
   */
  calculateDayReturnPercentage = (dayPnl: string, close: number) =>
    this.calculatePercentage(Number(dayPnl), close);

  /**
   * Calculate Overall P&L
   * @param  {string} currentValue
   * @param  {string} totalInvestment
   */
  calculateOverallPnL = (currentValue: string, totalInvestment: string) => {
    if (!totalInvestment || (totalInvestment && Number.isNaN(+totalInvestment))) return '--';

    return math.sub(Number(currentValue), Number(totalInvestment)).toFixed(2);
  };

  /**
   * Calculate overall percentage
   * @param  {string} overallPnl
   * @param  {string} totalInvestment
   */
  calculateOverallPercentage = (overallPnl: string, totalInvestment: string) =>
    this.calculatePercentage(Number(overallPnl), Number(totalInvestment));

  /**
   * Calculates the sum of Overall PnL
   * @param  {Holding[]} holdings array of holdings
   */
  getOverallPnlSum = (holdings: Holding[]) => {
    const sum = holdings.reduce((acc, holding) => {
      const { needToCalculateData, ltp } = this.calculationBaseData(holding);
      const netQnty = this.calculateNetQuantity(holding);
      const {
        overall = '0.00',
      } = needToCalculateData
        ? this.getHoldingPnlValues(netQnty, holding, ltp)
        : {};

      if (Number.isNaN(+overall)) return acc;

      return math.add(acc, Number(overall));
    }, 0);

    return FormatService.formatToIndianFloat(sum);
  };

  getHoldingPnlValues = (netQnty: number, holding: Holding, ltp: string) => {
    const avgPrice = holding?.netInfo?.netAvgPrice;
    const currentValue = this.calculateCurrValue(netQnty, ltp);
    const investment = holding?.netInfo?.netAmt?.toString();
    const close = this.calculateClose(holding, netQnty);
    const dayPnl = this.calculateDayPnL(currentValue, close);
    const dayPercentage = this.calculateDayReturnPercentage(dayPnl, close);
    const overall = this.calculateOverallPnL(currentValue, investment);
    const overallPercentage = this.calculateOverallPercentage(
      overall,
      investment,
    );

    return {
      avgPrice,
      currentValue,
      investment,
      close,
      dayPnl,
      dayPercentage,
      overall,
      overallPercentage,
    };
  };
}

export default new HoldingCalculations();
